import React, { ReactElement, useEffect, useState } from 'react';
import { Button, IconMediaStop, IconRecordActive } from '@seismic/mantle';
import { IRecordingState } from '../../models';
import { IRecordingStateReducerAction } from '../../hooks';
import styles from '../Footer.scss';
import { useTranslation } from 'react-i18next';
import DeleteConfirmationModal from '../shared/DeleteConfirmationModal';

const SecondsInMinute = 60;
const defaultMaxRecordTimeMinutes = 30;
const celingMaxRecordTimeMinutes = 40;

const toSeconds = (timeMinutes: number) => timeMinutes * SecondsInMinute;

interface Props {
  maxRecordTimeSeconds?: number;
  onCancel: () => void;
  state: IRecordingState;
  dispatch: React.Dispatch<IRecordingStateReducerAction>;
}

export default function RecordFooter(props: Props): ReactElement {
  const { t } = useTranslation();
  const { state } = props;
  const recorder = state.recorder;
  const [isRecording, setIsRecording] = useState(false);
  const [isDiscardOpen, setIsDiscardOpen] = useState<boolean>(false);
  const [lengthSeconds, setLengthMilliseconds] = React.useState(0);

  const getMaxRecordTime = () => {
    if (props.maxRecordTimeSeconds === undefined || props.maxRecordTimeSeconds <= 0) {
      return toSeconds(defaultMaxRecordTimeMinutes);
    }

    const ceilingSeconds = toSeconds(celingMaxRecordTimeMinutes);
    if (props.maxRecordTimeSeconds > ceilingSeconds) {
      return ceilingSeconds;
    }

    return props.maxRecordTimeSeconds;
  };
  const maxLengthSeconds = getMaxRecordTime();

  useEffect(() => {
    if (recorder) {
      recorder.onStart = () => {
        setIsRecording(true);
      };
    }
  }, [recorder]);

  useEffect(() => {
    let interval;
    if (isRecording) {
      interval = setInterval(() => {
        setLengthMilliseconds((previousTimeSeconds) => previousTimeSeconds + 1);
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [isRecording]);

  useEffect(() => {
    if (recorder && recorder.isRecording && lengthSeconds === maxLengthSeconds) {
      recorder.stopRecording();
    }
  }, [recorder, lengthSeconds, maxLengthSeconds]);

  const formatTime = (timeSeconds: number) => {
    const minutes = Math.floor(timeSeconds / SecondsInMinute);
    const seconds = timeSeconds - toSeconds(minutes);

    const minutesString = minutes.toString().padStart(2, '0');
    const secondsString = seconds.toString().padStart(2, '0');

    return `${minutesString}:${secondsString}`;
  };

  function onStop() {
    recorder.stopRecording();
  }

  const onDiscardOpen = () => setIsDiscardOpen(true);
  const onDiscardAccept = () => {
    setIsDiscardOpen(false);
    props.onCancel();
  };
  const onDiscardReject = () => setIsDiscardOpen(false);

  const timer = `${formatTime(lengthSeconds)} / ${formatTime(maxLengthSeconds)}`;

  return (
    <>
      <div className={styles.footer}>
        <div className={styles.recordIcon} style={{ display: 'flex', alignItems: 'center' }}>
          {isRecording ? (
            <>
              <IconRecordActive className={styles.pulse} />
              <div className={styles.timer} data-testid="timer">
                {timer}
              </div>
            </>
          ) : null}
        </div>
        {isRecording && (
          <Button
            label={t('Footer.Discard', { defaultValue: 'Discard' })}
            className={styles.discardButton}
            data-testid="discardButton"
            variant="tertiary"
            onClick={onDiscardOpen}
          />
        )}
        <Button
          label={t('Record.Footer.Stop', { defaultValue: 'Stop' })}
          data-testid="stop-recording-button"
          disabled={!isRecording}
          startAdornment={IconMediaStop}
          variant="primary"
          onClick={onStop}
        />
      </div>
      <DeleteConfirmationModal
        isOpen={isDiscardOpen}
        header={t('Modal.Discard.Header', { defaultValue: 'Discard Recording' })}
        description={t('Modal.Discard.Description', {
          defaultValue: 'Your current recording will not be saved and the recorder will close.'
        })}
        acceptButtonText={t('Footer.Discard', { defaultValue: 'Discard' })}
        onAccept={onDiscardAccept}
        onReject={onDiscardReject}
      />
    </>
  );
}
